import { useRef, type ReactNode, type MouseEvent } from "react";

interface MagneticButtonProps {
  href?: string;
  children: ReactNode;
  className?: string;
  strength?: number; // 0-1, how strongly it follows cursor
  onClick?: () => void;
}

export default function MagneticButton({
  href,
  children,
  className = "",
  strength = 0.35,
  onClick,
}: MagneticButtonProps) {
  const ref = useRef<HTMLAnchorElement | HTMLButtonElement | null>(null);
  const innerRef = useRef<HTMLSpanElement | null>(null);

  const handleMouseMove = (e: MouseEvent<HTMLElement>) => {
    const el = ref.current;
    const inner = innerRef.current;
    if (!el) return;
    const rect = el.getBoundingClientRect();
    const relX = e.clientX - (rect.left + rect.width / 2);
    const relY = e.clientY - (rect.top + rect.height / 2);
    const x = relX * strength;
    const y = relY * strength;
    el.style.transform = `translate3d(${x}px, ${y}px, 0)`;
    if (inner) {
      inner.style.transform = `translate3d(${x * 0.35}px, ${y * 0.35}px, 0)`;
    }
  };

  const handleMouseLeave = () => {
    const el = ref.current;
    const inner = innerRef.current;
    if (el) el.style.transform = "translate3d(0, 0, 0)";
    if (inner) inner.style.transform = "translate3d(0, 0, 0)";
  };

  const commonProps = {
    className: `${className} transition-transform duration-500 ease-[cubic-bezier(0.22,1,0.36,1)] will-change-transform`,
    onMouseMove: handleMouseMove,
    onMouseLeave: handleMouseLeave,
  };

  const inner = (
    <span
      ref={innerRef}
      className="inline-flex items-center justify-center gap-2 transition-transform duration-500 ease-[cubic-bezier(0.22,1,0.36,1)] will-change-transform"
    >
      {children}
    </span>
  );

  if (href) {
    return (
      <a
        ref={ref as React.RefObject<HTMLAnchorElement>}
        href={href}
        {...commonProps}
      >
        {inner}
      </a>
    );
  }

  return (
    <button
      ref={ref as React.RefObject<HTMLButtonElement>}
      onClick={onClick}
      {...commonProps}
    >
      {inner}
    </button>
  );
}